import { NAVPAGE_OBJECT } from "@/constants/routePath";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import SupabasePool from "@/lib/supabaseClient";
import { toast } from "sonner";
import { LogOut, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useGSAP } from "@gsap/react";
import SubNav from "@/components/shared/sub-nav";
import { useAuthActions, useAuthentication } from "@/hooks/useAuth";

const RootNavList = () => {
  const authentication = useAuthentication();
  const { removeAuth } = useAuthActions();
  const [view, setView] = useState(false);
  const menuRefs = useRef<HTMLDivElement[]>([]);
  const menuWrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPc = useMediaQuery(`(min-width:1024px)`);

  useGSAP(() => {
    if (!headerRef.current || pathname != "/") return;

    gsap.from(headerRef.current, {
      delay: 2.4,
      opacity: 0,
    });
  }, {});

  // PC에선 모바일메뉴 닫기
  useEffect(() => {
    if (isPc) closeMenu();
  }, [isPc]);

  const { mutateAsync } = useMutation({
    mutationFn: async () => {
      await SupabasePool.getInstance().auth.signOut();
    },
    onSuccess: () => {
      toast.info("로그아웃 되었습니다");
      removeAuth();
    },
  });

  const openMenu = () => {
    setView(true);

    const tl = gsap.timeline({});

    tl.fromTo(
      menuWrapperRef.current,
      { x: "100%" },
      { x: "0%", duration: 0.4, ease: "power2.out" },
      "<"
    ).fromTo(
      menuRefs.current,
      { y: -80, opacity: 0 },
      { y: 0, opacity: 1, stagger: 0.05, duration: 0.5, ease: "power2.out" },
      "-=0.2"
    );
  };

  const closeMenu = () => {
    const tl = gsap.timeline({
      onComplete: () => {
        setView(false);
      },
    });

    tl.to(menuRefs.current, {
      y: 50,
      opacity: 0,
      stagger: 0.03,
      duration: 0.25,
      ease: "power2.in",
    }).to(menuWrapperRef.current, {
      x: "100%",
      duration: 0.35,
      ease: "power2.inOut",
    });
  };

  return (
    <>
      {view && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-800 h-screen"
          onClick={closeMenu}
        />
      )}

      <div
        ref={menuWrapperRef}
        className={cn(
          "fixed right-0 top-0 w-4/5 md:w-2/4 max-w-[600px] h-screen z-900 bg-zinc-900 text-zinc-100 px-10 pt-10 pb-20 flex flex-col shadow-2xl ",
          !view && "translate-x-full"
        )}
        style={{
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="flex justify-end mb-10">
          <X className="size-10 cursor-pointer" onClick={closeMenu} />
        </div>

        <div className="flex flex-col gap-8 text-right mt-10">
          {NAVPAGE_OBJECT.map((e, idx) => {
            if (!authentication && e.AuthPage) return null;
            return (
              <div
                key={idx}
                ref={(el) => (menuRefs.current[idx] = el!)}
                className={cn(
                  "text-3xl md:text-4xl font-montserrat cursor-pointer hover:text-pink-400 transition-colors",
                  e.path === pathname && "text-pink-400 underline"
                )}
                onClick={() => {
                  if (e.path === pathname) return;
                  if (!e.AuthPage || authentication) {
                    navigate(e.path);
                    closeMenu();
                  }
                }}
              >
                {e.pathName}
              </div>
            );
          })}
          {authentication && (
            <span
              className="text-3xl md:text-3xl font-montserrat cursor-pointer hover:text-pink-400 transition-colors"
              onClick={async () => {
                await mutateAsync();
                closeMenu();
              }}
            >
              LogOut
            </span>
          )}{" "}
          <div className="ml-auto">
            <SubNav />
          </div>
        </div>
      </div>

      <nav
        className="  grid grid-cols-[auto_1fr] gap-20 py-1   w-full"
        ref={headerRef}
      >
        <button onClick={() => navigate("/")}>Phm' Portfolio</button>
        <div className=" gap-10  md:flex hidden  w-full">
          {NAVPAGE_OBJECT.map((e, idx) => {
            if (e.AuthPage && !authentication) return;

            return (
              <button
                key={`${e.pathName}:${idx}`}
                className={cn(
                  "text-sm opacity-60 hover:opacity-100",
                  e.path.split("/")[1] === pathname.split("/")[1] &&
                    "border-b opacity-100"
                )}
                onClick={() => navigate(e.path)}
              >
                {e.pathName}
              </button>
            );
          })}
          <div className="ml-auto flex">
            <SubNav />
            {authentication && (
              <div
                onClick={async () => {
                  await mutateAsync();
                  closeMenu();
                }}
                className="bg-transparent! ml-2  transition-all duration-100 group p-2 h-auto rounded-lg border  border-zinc-50/10 hover:border-indigo-200/50"
              >
                <div className="relative">
                  <LogOut className="size-3.5 md:size-3.5 fill-foreground opacity-50 group-hover:opacity-100 group-hover:fill-indigo-200 transition-all" />
                </div>
              </div>
            )}{" "}
          </div>
        </div>

        <div
          className="group cursor-pointer relative  items-center justify-center md:hidden flex ml-auto "
          onClick={openMenu}
        >
          {/* 메인 버튼 */}
          <div className="relative size-full rounded-lg  backdrop-blur-sm flex items-center justify-center group-hover:border-slate-400/70 transition-all duration-300">
            {/* 햄버거 아이콘 */}
            <div className="flex flex-col gap-1.5 items-center justify-center">
              <span className="w-6 h-0.5 bg-slate-300 rounded-full group-hover:bg-slate-100 transition-colors duration-300" />
              <span className="w-6 h-0.5 bg-slate-300 rounded-full group-hover:bg-slate-100 transition-colors duration-300" />
              <span className="w-6 h-0.5 bg-slate-300 rounded-full group-hover:bg-slate-100 transition-colors duration-300" />
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default RootNavList;
